import api from './api';

class NotificationService {
  /**
   * Get notifications for the current user
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {boolean} params.unread_only - Filter to unread notifications only
   * @returns {Promise} API response
   */
  async getNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.unread_only) queryParams.append('unread_only', 'true');

      const queryString = queryParams.toString();
      const url = `/notifications/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise} API response with unread count
   */
  async getUnreadCount() {
    try {
      const response = await api.get('/notifications/unread-count/');
      return response.data;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - UUID of the notification
   * @returns {Promise} API response
   */
  async markAsRead(notificationId) {
    try {
      const response = await api.patch(`/notifications/${notificationId}/mark-read/`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific notification
   * @param {string} notificationId - UUID of the notification
   * @returns {Promise} API response
   */
  async getNotificationDetail(notificationId) {
    try {
      const response = await api.get(`/notifications/${notificationId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification detail:', error);
      throw error;
    }
  }

  /**
   * Get notification type icon based on notification type
   * @param {string} type - Notification type
   * @returns {string} Icon name for Material-UI
   */
  getNotificationIcon(type) {
    const iconMap = {
      'order_created': 'ShoppingCart',
      'order_updated': 'Update',
      'order_completed': 'CheckCircle',
      'order_cancelled': 'Cancel',
      'payment_received': 'Payment',
      'payment_failed': 'Error',
      'system_notification': 'Info',
      'table_request': 'TableRestaurant',
      'menu_updated': 'RestaurantMenu',
      'default': 'Notifications'
    };

    return iconMap[type] || iconMap.default;
  }

  /**
   * Get notification color based on notification type
   * @param {string} type - Notification type
   * @returns {string} Color for the notification
   */
  getNotificationColor(type) {
    const colorMap = {
      'order_created': 'primary',
      'order_updated': 'info',
      'order_completed': 'success',
      'order_cancelled': 'error',
      'payment_received': 'success',
      'payment_failed': 'error',
      'system_notification': 'info',
      'table_request': 'warning',
      'menu_updated': 'info',
      'default': 'default'
    };

    return colorMap[type] || colorMap.default;
  }

  /**
   * Format notification for display
   * @param {Object} notification - Raw notification object
   * @returns {Object} Formatted notification
   */
  formatNotification(notification) {
    return {
      ...notification,
      icon: this.getNotificationIcon(notification.notification_type),
      color: this.getNotificationColor(notification.notification_type),
      formattedTime: notification.time_ago || 'Just now'
    };
  }

  /**
   * Format multiple notifications for display
   * @param {Array} notifications - Array of raw notification objects
   * @returns {Array} Array of formatted notifications
   */
  formatNotifications(notifications) {
    return notifications.map(notification => this.formatNotification(notification));
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
